// ============= Imports =============
'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { isAuthBypassed } from '../authConfig';
import { tokenManager } from '../utils/tokenManager';

// ============= Types =============
export interface AuthContextProps {
  isAuthenticated: boolean;
  activeAccount: AccountInfo | null;
  accessToken: string | null;
  login: () => void;
  logout: () => void;
  getAccessToken: (
    scopes?: string[],
    options?: { skipStorageToken?: boolean }
  ) => Promise<string | null>;
}

interface AuthState {
  isAuthenticated: boolean;
  activeAccount: AccountInfo | null;
  accessToken: string | null;
}
// TODO: Implement token cache logic
// interface TokenCache {
//   [key: string]: {
//     token: string;
//     expiresOn: string;
//     scopes: string[];
//   };
// }

interface TokenResponse {
  token: string;
  expiresOn: Date;
}

// ============= Constants =============
const DEFAULT_SCOPES = ['openid', 'profile'];
const TOKEN_CACHE_KEY = 'tokenCache';
const TOKEN_EXPIRATION = 3600 * 1000; // 1 hour in milliseconds
const TOKEN_CHECK_INTERVAL = 60000; // Check every 30 seconds

// ============= Context =============
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

// ============= Hook =============
export const UseAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('UseAuth must be used within an AuthProvider');
  }
  return context;
};

// ============= Provider Component =============
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { instance, accounts, inProgress } = useMsal();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    activeAccount: null,
    accessToken: null,
  });
// TODO: Implement token cache logic
  // const getTokenCache = useCallback((): TokenCache => {
  //   try {
  //     return JSON.parse(sessionStorage.getItem(TOKEN_CACHE_KEY) || '{}');
  //   } catch {
  //     return {};
  //   }
  // }, []);

  // const setTokenCache = useCallback((cache: TokenCache): void => {
  //   sessionStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(cache));
  // }, []);

  const clearTokenCache = useCallback((): void => {
    sessionStorage.removeItem(TOKEN_CACHE_KEY);
  }, []);

  const acquireToken = useCallback(
    async (
      account: AccountInfo,
      scopes: string[],
      forceRefresh = false
    ): Promise<TokenResponse | null> => {
      try {
        const response = await instance.acquireTokenSilent({
          account,
          scopes,
          forceRefresh,
        });

        const expiresOn =
          response.expiresOn || new Date(Date.now() + TOKEN_EXPIRATION);

        tokenManager.logTokenInfo(response.accessToken, scopes.join(', '));

        return {
          token: response.accessToken,
          expiresOn,
        };
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const response = await instance.acquireTokenPopup({ scopes });
            return {
              token: response.accessToken,
              expiresOn:
                response.expiresOn || new Date(Date.now() + TOKEN_EXPIRATION),
            };
          } catch (popupError) {
            console.error('Error acquiring token:', popupError);
            await handleTokenError();
            return null;
          }
        }
        console.error('Error acquiring token silently:', error);
        await handleTokenError();
        return null;
      }
    },
    [instance]
  );

  const handleTokenError = async () => {
    clearTokenCache();
    setAuthState({
      isAuthenticated: false,
      activeAccount: null,
      accessToken: null,
    });
    await login();
  };
// TODO: Implement refresh token logic
  // const refreshToken = useCallback(
  //   async (
  //     account: AccountInfo,
  //     scopes: string[],
  //     cacheKey: string
  //   ): Promise<void> => {
  //     const tokenResponse = await acquireToken(account, scopes, true);
  //     if (tokenResponse) {
  //       const tokenCache = getTokenCache();
  //       const newCache = {
  //         ...tokenCache,
  //         [cacheKey]: {
  //           token: tokenResponse.token,
  //           expiresOn: tokenResponse.expiresOn.toISOString(),
  //           scopes,
  //         },
  //       };
  //       setTokenCache(newCache);

  //       if (cacheKey === DEFAULT_SCOPES.sort().join('|')) {
  //         setAuthState((prev) => ({
  //           ...prev,
  //           accessToken: tokenResponse.token,
  //         }));
  //       }
  //     }
  //   },
  //   [acquireToken, getTokenCache, setTokenCache]
  // );

const getAccessToken = useCallback(
  async (
    scopes?: string[],
    options: { skipStorageToken?: boolean } = {}
  ): Promise<string | null> => {
    if (isAuthBypassed) return 'mock-token';
    if (!authState.activeAccount) return null;

    // Force skipStorageToken to true
    options.skipStorageToken = true;

    // Check if we should skip storage token check
    if (!options.skipStorageToken) {
      // Check if current MSAL token is valid
      if (tokenManager.validateMsalToken()) {
        const tokenData = tokenManager.getMsalTokenFromStorage();
        return tokenData?.secret || null;
      }
    }

    // If token is invalid, expired, or skip is true, acquire new token
    const scopesToUse = scopes || DEFAULT_SCOPES;
    const tokenResponse = await acquireToken(
      authState.activeAccount,
      scopesToUse
    );

    if (tokenResponse) {
      return tokenResponse.token;
    }

    return null;
  },
  [authState.activeAccount, acquireToken]
);

  const login = async () => {
    if (isAuthBypassed) {
      console.log('Bypass mode: Login not required');
      return;
    }
    try {
      await instance.loginRedirect({
        scopes: DEFAULT_SCOPES,
        prompt: 'select_account',
      });
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const logout = async () => {
    if (isAuthBypassed) {
      console.log('Bypass mode: Logout not required');
      return;
    }
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setAuthState({
        isAuthenticated: false,
        activeAccount: null,
        accessToken: null,
      });
      clearTokenCache();
    }
  };

  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const checkToken = () => {
      const isValid = tokenManager.checkAndHandleTokenExpiration();
      if (!isValid) {
        // Token was expired and cleared, trigger new token acquisition
        void getAccessToken();
      }
    };

    // Check immediately
    checkToken();

    // Set up interval to check periodically
    const interval = setInterval(checkToken, TOKEN_CHECK_INTERVAL); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, getAccessToken]);

  useEffect(() => {
    if (isAuthBypassed) {
      const mockAccount: AccountInfo = {
        homeAccountId: 'mock-home-account-id',
        environment: 'mock-environment',
        tenantId: 'mock-tenant-id',
        username: 'mockuser@domain.com',
        localAccountId: 'mock-local-id',
        idTokenClaims: {
          name: 'Mock User',
          oid: 'mock-oid',
        },
      };
      setAuthState({
        isAuthenticated: true,
        activeAccount: mockAccount,
        accessToken: 'mock-token',
      });
      return;
    }

    const currentAccount = accounts[0];

    const updateAuthState = async () => {
      if (currentAccount && inProgress === 'none') {
        const tokenResponse = await acquireToken(
          currentAccount,
          DEFAULT_SCOPES
        );
        setAuthState({
          isAuthenticated: true,
          activeAccount: currentAccount,
          accessToken: tokenResponse?.token || null,
        });
      } else if (!currentAccount && inProgress === 'none') {
        setAuthState({
          isAuthenticated: false,
          activeAccount: null,
          accessToken: null,
        });
      }
    };

    void updateAuthState();
  }, [accounts, inProgress, instance, acquireToken]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: authState.isAuthenticated,
        activeAccount: authState.activeAccount,
        accessToken: authState.accessToken,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
